import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useChatOps, UseChatOpsProps } from "../hooks/useChatOps";
import { ChatOpsFormSkeleton } from "../components/ChatOpsFormSkeleton";
import { useEffect } from "react";
import { ChatOpsFormBase } from "../components/ChatOpsFormBase";

interface MsTeamsFormValues {
  activeState: boolean;
  url: string;
}

export function ChatOpsMsTeamsForm(props: UseChatOpsProps) {
  const {
    integration,
    saveIntegration,
    hasChatOpsPermission,
    handleUrlChange,
  } = useChatOps(props);
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    reset,
    watch,
    setValue,
  } = useForm<MsTeamsFormValues>({
    defaultValues: {
      activeState: false,
      url: "",
    },
    mode: "onChange",
  });

  const [activeState, url] = watch(["activeState", "url"]);

  useEffect(() => {
    if (integration) {
      reset({
        activeState: integration.enabled ?? false,
        url: integration.options?.url ?? "",
      });
    }
  }, [integration]);

  // if there is no url and then the user updates the url, set the activeState to true
  useEffect(() => {
    handleUrlChange(url, activeState, setValue);
  }, [url]);

  const onSubmit = (data: MsTeamsFormValues) => {
    if (!integration) return;

    const variables = {
      input: {
        mrn: integration.mrn,
        name: integration.name,
        enabled: data.activeState,
        options: {
          ...integration.options,
          url: data.url,
        },
      },
    };
    saveIntegration(variables);
  };

  if (!integration) {
    return <ChatOpsFormSkeleton height={275} />;
  }

  return (
    <ChatOpsFormBase
      baseSubmit={handleSubmit(onSubmit)}
      isDirty={isDirty}
      isValid={isValid}
      hasPermission={hasChatOpsPermission}
    >
      <Grid size={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Controller
          control={control}
          name="activeState"
          render={({ field }) => (
            <FormControlLabel
              labelPlacement="start"
              control={
                <Switch
                  color="primary"
                  checked={field.value}
                  onChange={field.onChange}
                />
              }
              label={field.value ? "Enabled" : "Disabled"}
            />
          )}
        />
      </Grid>
      <Grid size={12}>
        <Controller
          control={control}
          name="url"
          rules={{
            required: activeState ? "URL is required" : false,
          }}
          render={({ field }) => (
            <FormControl fullWidth>
              <FormLabel>
                <Typography variant="h6" color="textPrimary">
                  URL
                </Typography>
              </FormLabel>
              <TextField
                {...field}
                fullWidth
                error={!!errors.url && activeState}
                helperText={activeState ? errors.url?.message : ""}
              />
            </FormControl>
          )}
        />
      </Grid>
    </ChatOpsFormBase>
  );
}
